export type FullConnectomeSex = 'male' | 'female';

export type FullConnectomeId = 'mcns-v1.0' | 'banc-v888';

export interface FullConnectomeManifest {
  readonly id: FullConnectomeId;
  readonly sex: FullConnectomeSex;
  readonly dataset: string;
  readonly neurons: number;
  readonly connections: number;
  readonly graphFile?: string;
  readonly graphBytes?: number;
  readonly sourceUrl: string;
  readonly downloadUrl: string;
  readonly viewerUrl: string;
  readonly license: string;
  readonly scope: 'brain-and-nerve-cord';
  readonly mode: 'official-viewer';
}

export const FULL_CONNECTOME_MANIFESTS: Readonly<Record<FullConnectomeSex, FullConnectomeManifest>> = {
  male: {
    id: 'mcns-v1.0',
    sex: 'male',
    dataset: 'MaleCNS v1.0 — male adult fly brain and nerve cord',
    neurons: 166_700,
    connections: 6_242_118,
    graphFile: 'connectome-weights-male-cns-v1.0-minconf-0.5.feather',
    graphBytes: 1_100_000_000,
    sourceUrl: 'https://male-cns.janelia.org/download/',
    downloadUrl: 'https://storage.googleapis.com/flyem-male-cns/v1.0/connectome-data/flat-connectome/connectome-weights-male-cns-v1.0-minconf-0.5.feather',
    viewerUrl: 'https://neuroglancer-demo.appspot.com/#!gs://flyem-male-cns/v1.0/male-cns-v1.0.json',
    license: 'CC BY 4.0',
    scope: 'brain-and-nerve-cord',
    mode: 'official-viewer',
  },
  female: {
    id: 'banc-v888',
    sex: 'female',
    dataset: 'FlyWire BANC v888 — female adult fly brain and nerve cord',
    neurons: 158_262,
    connections: 3_037_361,
    sourceUrl: 'https://codex.flywire.ai/api/',
    downloadUrl: 'https://codex.flywire.ai/api/download',
    viewerUrl: 'https://codex.flywire.ai/app/view_3d',
    license: 'FlyWire terms and citation requirements apply',
    scope: 'brain-and-nerve-cord',
    mode: 'official-viewer',
  },
};

export interface FullConnectomeSession {
  readonly manifest: FullConnectomeManifest;
  readonly openedAt: number;
  readonly webGpuAvailable: boolean;
  readonly message: string;
}

export function getFullConnectomeManifest(sex: FullConnectomeSex): FullConnectomeManifest {
  return FULL_CONNECTOME_MANIFESTS[sex];
}

export function validateFullConnectomeManifest(manifest: FullConnectomeManifest): string[] {
  const errors: string[] = [];

  if (manifest.neurons <= 0) errors.push('neurons must be positive');
  if (manifest.connections <= 0) errors.push('connections must be positive');
  if (manifest.scope !== 'brain-and-nerve-cord') errors.push('scope must cover brain and nerve cord');
  if (manifest.mode !== 'official-viewer') errors.push('unsupported browser mode');
  if (!manifest.sourceUrl.startsWith('https://')) errors.push('sourceUrl must use HTTPS');
  if (!manifest.downloadUrl.startsWith('https://')) errors.push('downloadUrl must use HTTPS');
  if (!manifest.viewerUrl.startsWith('https://')) errors.push('viewerUrl must use HTTPS');

  return errors;
}

export async function openFullConnectome(sex: FullConnectomeSex): Promise<FullConnectomeSession> {
  const manifest = getFullConnectomeManifest(sex);
  const errors = validateFullConnectomeManifest(manifest);

  if (errors.length > 0) {
    throw new Error(`Invalid ${manifest.id} manifest: ${errors.join(', ')}`);
  }

  return {
    manifest,
    openedAt: Date.now(),
    webGpuAvailable: typeof navigator !== 'undefined' && 'gpu' in navigator,
    message:
      'Manifest validated: dataset id, neuron/connection counts, licence and official URLs are real and readable offline. ' +
      'No connectome data is served from this page — the source graph stays on the official infrastructure and is reached only by ' +
      'opening the viewer or data portal below.',
  };
}
